/**
 * LIFF wrapper — provides typed access to LINE LIFF SDK.
 * Falls back to mock in dev (when not running inside LINE).
 */
import liff from '@line/liff';

const LIFF_ID = import.meta.env.VITE_LIFF_ID ?? '';
const USE_MOCK = !LIFF_ID || import.meta.env.DEV;

interface LiffShim {
  isLoggedIn: () => boolean;
  login: (opts?: { redirectUri?: string }) => void;
  logout: () => void;
  getIDToken: () => string | null;
  getProfile: () => Promise<{ userId: string; displayName: string; pictureUrl?: string }>;
  closeWindow: () => void;
  scanCodeV2?: () => Promise<{ value: string }>;
}

const mockProfile = {
  userId: 'U_mock_dev_user',
  displayName: 'Dev User (Mock)',
  pictureUrl: undefined,
};

const mockShim: LiffShim = {
  isLoggedIn: () => true,
  login: () => console.log('[mock] login'),
  logout: () => console.log('[mock] logout'),
  getIDToken: () => 'mock_id_token_for_local_dev',
  getProfile: async () => mockProfile,
  closeWindow: () => console.log('[mock] closeWindow'),
};

let activeShim: LiffShim = mockShim;
let initialized = false;

export async function initLiff(): Promise<void> {
  if (initialized) return;

  if (USE_MOCK) {
    activeShim = mockShim;
    initialized = true;
    return;
  }

  await liff.init({ liffId: LIFF_ID });
  if (!liff.isLoggedIn()) {
    liff.login();
    return;
  }
  activeShim = liff as unknown as LiffShim;
  initialized = true;
}

export function getIDToken(): string | null {
  return activeShim.getIDToken();
}

export async function getProfile() {
  return activeShim.getProfile();
}

export function logout() {
  activeShim.logout();
}

export function closeWindow() {
  activeShim.closeWindow();
}

export async function scanQR(): Promise<string | null> {
  if (activeShim.scanCodeV2) {
    const result = await activeShim.scanCodeV2();
    return result.value;
  }
  return null;
}

export const isMock = USE_MOCK;
